import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App.tsx";
import { initLifecycle } from "./lib/lifecycle.ts";
import {
  initErrorTracking,
  setupGlobalErrorHandlers,
  errorTracking,
} from "./lib/error-tracking.ts";
import { ENV_CONFIG, SENTRY_CONFIG } from "./config.ts";
import { pwaLog } from "./lib/logger.ts";
import "./index.css";

// =============================================================================
// INITIALIZATION
// =============================================================================

// 1. Initialize Error Tracking (Sentry) — do this first to catch all errors
initErrorTracking({
  dsn: SENTRY_CONFIG.dsn,
  environment: ENV_CONFIG.mode,
  release: ENV_CONFIG.version,
  enableInDev: SENTRY_CONFIG.enableInDev,
  sampleRate: SENTRY_CONFIG.sampleRate,
  tracesSampleRate: SENTRY_CONFIG.tracesSampleRate,
});

// 2. Setup Global Error Handlers
setupGlobalErrorHandlers();

// 3. Initialize lifecycle (PWA visibility, etc.)
initLifecycle();

// 4. PWA: register SW in background. Do NOT block first paint.
// Using registerType: "prompt" so updates require user action (no auto-reload on visibility change).
if ("serviceWorker" in navigator) {
  import("virtual:pwa-register")
    .then(({ registerSW }) => {
      const updateSW = registerSW({
        onNeedRefresh: () => {
          // New SW available — show update banner, let user decide when to reload
          window.dispatchEvent(new CustomEvent("pwa-update"));
        },
        onRegisteredSW: (
          _url: string,
          registration: ServiceWorkerRegistration | undefined
        ) => {
          if (registration?.active && ENV_CONFIG.isDev) {
            pwaLog.debug("Service Worker is active");
          }
        },
        onRegisterError: (error: Error) => {
          // Report SW registration failures to Sentry
          // This can happen due to:
          // - HTTPS requirement (SW only works on https/localhost)
          // - Browser restrictions (private browsing, disabled SW)
          // - Corrupted SW cache
          pwaLog.error("Service Worker registration failed:", error);
          errorTracking.captureError(error, {
            tags: { component: "service-worker", action: "registration" },
            extra: { errorMessage: error.message },
          });
        },
      });
      // Expose updateSW for PwaUpdateBanner to trigger controlled reload
      window.__pwaUpdateServiceWorker = updateSW;
    })
    .catch((error: Error) => {
      // Failed to load pwa-register module
      pwaLog.error("Failed to load Service Worker module:", error);
    });
}

// 5. Initialize Telegram WebApp (optional — PWA works without Telegram)
const tg = window.Telegram?.WebApp;
if (tg) {
  tg.ready();
  tg.expand();
}

// 6. Render immediately so user sees SplashScreen/content fast. Auth reads tokens (localStorage first).
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);
